/**
 * KeyboardShortcutsHelp — the "?" overlay listing global shortcuts.
 */
import React, { useEffect } from 'react';
import { theme, Kbd } from '@turn-connect/ui';
import { QUICK_KEYS_OVERLAY_Z } from './CommandPalette';

const isMac = typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.platform || '');

interface KeyboardShortcutsHelpProps {
  isOpen: boolean;
  onClose: () => void;
}

const SHORTCUTS: Array<{ keys: React.ReactNode; label: string }> = [
  {
    keys: (
      <>
        <Kbd>{isMac ? '⌘' : 'Ctrl'}</Kbd>+<Kbd>K</Kbd> / <Kbd>/</Kbd>
      </>
    ),
    label: 'Search, recents & actions',
  },
  { keys: <Kbd>n</Kbd>, label: 'New work order' },
  {
    keys: (
      <>
        <Kbd>g</Kbd> then <Kbd>l</Kbd>
      </>
    ),
    label: 'Reopen last work order',
  },
  { keys: <Kbd>Esc</Kbd>, label: 'Close topmost overlay' },
  { keys: <Kbd>?</Kbd>, label: 'This help' },
];

const KeyboardShortcutsHelp: React.FC<KeyboardShortcutsHelpProps> = ({ isOpen, onClose }) => {
  // Capture-phase Escape so closing the help never also closes a work order
  // detail modal underneath (Modal listens for Escape on document bubble).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: QUICK_KEYS_OVERLAY_Z, backgroundColor: 'rgba(0, 0, 0, 0.62)' }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-label="Keyboard shortcuts"
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(26rem, calc(100vw - 2rem))',
          backgroundColor: theme.colors.background.secondary,
          border: `1px solid ${theme.colors.border.light}`,
          borderRadius: '0.625rem',
          boxShadow: '0 18px 50px rgba(0, 0, 0, 0.55)',
          padding: '1.125rem 1.25rem 1rem',
        }}
      >
        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '0.75rem' }}>
          Keyboard shortcuts
        </div>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            {SHORTCUTS.map((s, i) => (
              <tr key={i}>
                <td
                  style={{
                    padding: '0.375rem 0',
                    borderBottom: i < SHORTCUTS.length - 1 ? `1px solid ${theme.colors.border.default}` : 'none',
                    width: '9.5rem',
                    whiteSpace: 'nowrap',
                    color: theme.colors.text.secondary,
                    fontSize: '0.75rem',
                  }}
                >
                  {s.keys}
                </td>
                <td
                  style={{
                    padding: '0.375rem 0',
                    borderBottom: i < SHORTCUTS.length - 1 ? `1px solid ${theme.colors.border.default}` : 'none',
                    color: theme.colors.text.secondary,
                    fontSize: '0.75rem',
                  }}
                >
                  {s.label}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ color: theme.colors.text.tertiary, fontSize: '0.6875rem', marginTop: '0.75rem' }}>
          Shortcuts pause while you're typing in a field or a dialog is open. Press <Kbd>Esc</Kbd> to close.
          <br />
          Shortcuts are plain keys — combos like <Kbd>{isMac ? '⌘' : 'Ctrl'}</Kbd>+<Kbd>N</Kbd> belong to the browser (new window) and never
          reach the app.
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcutsHelp;
